import { useState } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { Project } from '#shared/projects'
import { reorderedIds } from '#shared/ordering'
import { api, HttpError } from '../api/client'
import { SortableRow } from './SortableRow'

interface Props {
  /** Every project, archived ones included. */
  projects: Project[]
  /** False for members: the same list, read-only. */
  canManage: boolean
  onChanged: () => void | Promise<void>
}

/**
 * The project list.
 *
 * Managers get drag ordering, rename and archive; members get the same list
 * without the controls. Tags used to hang off these rows as features; they
 * are global now and live in their own card, because nesting a shared
 * vocabulary under one project would misstate what it is.
 */
export function ProjectManager({ projects, canManage, onChanged }: Props) {
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState({ name: '', description: '', allowsTags: true })

  // Mirrors the server order while a save is in flight, so the row stays where
  // it was dropped instead of snapping back and then jumping forward.
  const [pending, setPending] = useState<Project[] | null>(null)

  const source = pending ?? projects
  const active = source.filter((c) => !c.archived)
  const archived = source.filter((c) => c.archived)

  const sensors = useSensors(
    // A few pixels of movement before a drag starts, so clicking a button
    // inside the row is never mistaken for the beginning of a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function fail(err: unknown, fallback: string) {
    setError(err instanceof HttpError ? err.message : fallback)
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active: dragged, over } = event
    if (!over || dragged.id === over.id) return

    const ids = reorderedIds(active, archived, String(dragged.id), String(over.id))
    const byId = new Map(source.map((c) => [c.id, c]))

    // Show the new order immediately; the request confirms or undoes it.
    setPending(ids.map((id) => byId.get(id)!).filter(Boolean))

    try {
      await api.reorderProjects(ids)
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not save the new order.')
    } finally {
      setPending(null)
    }
  }


  async function handleSaveEdit(id: string) {
    try {
      await api.updateProject(id, draft)
      setEditing(null)
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not save that project.')
    }
  }

  async function handleArchive(project: Project) {
    try {
      const usage = await api.projectUsage(project.id)
      const warning =
        usage.entries > 0
          ? `${project.name} has ${usage.entries} ${usage.entries === 1 ? 'entry' : 'entries'} logged against it. Archiving hides the project from the log-time picker; those entries are kept and still counted in reports. Continue?`
          : `Archive ${project.name}? Nothing has been logged against it yet, and it disappears from the log-time picker.`

      if (!window.confirm(warning)) return

      await api.updateProject(project.id, { archived: true })
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not archive that project.')
    }
  }

  async function handleRestore(project: Project) {
    try {
      await api.updateProject(project.id, { archived: false })
      await onChanged()
    } catch (err) {
      fail(err, 'Could not restore that project.')
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Projects</h2>
        <span className="count">{active.length} active</span>
      </div>
      {canManage && (
        <p className="muted card-subtitle">
          Drag a project to reorder it — this is the order everyone sees when logging time.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      {!canManage ? (
        <ul className="rows">
          {active.map((project) => (
            <li key={project.id} className="row row-project">
              <div className="row-body">
                <span className="row-name">
                  {project.name}
                  {!project.allowsTags && <span className="pill">No tags</span>}
                </span>
                <span className="row-desc">{project.description}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={active.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <ul className="rows">
              {active.map((project) => (
                <SortableRow
                  key={project.id}
                  id={project.id}
                  label={project.name}
                  editing={editing === project.id}
                  actions={
                    editing === project.id ? undefined : (
                      <>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => {
                            setEditing(project.id)
                            setDraft({
                              name: project.name,
                              description: project.description,
                              allowsTags: project.allowsTags,
                            })
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-danger"
                          onClick={() => handleArchive(project)}
                        >
                          Archive
                        </button>
                      </>
                    )
                  }
                >
                  {editing === project.id ? (
                    <div className="inline-edit">
                      <input
                        value={draft.name}
                        maxLength={120}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        aria-label="Project name"
                      />
                      <textarea
                        rows={2}
                        maxLength={2000}
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        aria-label="Project description"
                      />
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={draft.allowsTags}
                          onChange={(e) => setDraft({ ...draft, allowsTags: e.target.checked })}
                        />
                        <span>Allow tags</span>
                      </label>
                      <div className="row-actions">
                        <button type="button" onClick={() => handleSaveEdit(project.id)}>
                          Save
                        </button>
                        <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="row-name">
                        {project.name}
                        {!project.allowsTags && <span className="pill">No tags</span>}
                      </span>
                      <span className="row-desc">{project.description}</span>
                    </>
                  )}
                </SortableRow>
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {canManage && archived.length > 0 && (
        <details className="archived">
          <summary className="muted">{archived.length} archived</summary>
          <ul className="rows">
            {archived.map((project) => (
              <li key={project.id} className="row is-archived">
                <div className="row-body">
                  <span className="row-name">{project.name}</span>
                  <span className="row-desc">{project.description}</span>
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => handleRestore(project)}
                  >
                    Restore
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
